import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { Mic, PhoneIncoming, PhoneMissed, PhoneOutgoing, Users, Voicemail } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { get, post, qs } from "../../api/client";
import { missedCallsKey, useMissedCalls } from "../../layout/useBadgeCounts";
import type { CallListItem, InboxNumber, Member, Page, Team } from "../../api/types";
import { useCan, useSession } from "../../auth/session";
import { Button } from "../../components/ui/Button";
import { SelectField, TextField } from "../../components/ui/Field";
import { Badge, Card, EmptyState, ErrorState, Loading, PageHeader, Pagination, Table, Td, Th, type Tone } from "../../components/ui/misc";
import { STATUS_LABELS, endReasonLabel, formatDateTime, formatDuration, formatPhone } from "../../lib/format";
import { useDialer } from "../../voice/dialer";

export const statusTone = (c: Pick<CallListItem, "status" | "missed">): Tone =>
  c.missed ? "red" : c.status === "COMPLETED" ? "green" : ["FAILED", "BUSY", "NO_ANSWER", "CANCELED"].includes(c.status) ? "amber" : "blue";

export function HistoryPage() {
  const { can, canAny } = useCan();
  const { data: session } = useSession();
  const qc = useQueryClient();
  // Looking at call history clears the missed-calls badge, including calls missed while you're here.
  const missed = useMissedCalls(true);
  const unseen = missed.data?.unseen;
  const markedOnce = useRef(false);
  useEffect(() => {
    if (markedOnce.current && !unseen) return; // once on opening, then again only if new ones arrive
    markedOnce.current = true;
    void post("/calls/missed/seen").then(() => qc.setQueryData(missedCallsKey, { unseen: 0 })).catch(() => undefined);
  }, [unseen, qc]);
  const dial = useDialer((s) => s.dial);
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "missed" ? "missed" : "all";
  const [searchInput, setSearchInput] = useState(params.get("search") ?? "");
  const filters = {
    direction: params.get("direction") ?? "",
    status: params.get("status") ?? "",
    membershipId: params.get("membershipId") ?? "",
    teamId: params.get("teamId") ?? "",
    phoneNumberId: params.get("phoneNumberId") ?? "",
    from: params.get("from") ?? "",
    to: params.get("to") ?? "",
    search: params.get("search") ?? "",
    sort: params.get("sort") ?? "createdAt",
    page: Number(params.get("page") ?? "1") || 1,
  };
  // Always merge into the latest URL params so debounced and immediate filter changes don't overwrite each other.
  const latest = useRef({ params, setParams });
  useEffect(() => {
    latest.current = { params, setParams };
  });
  const update = (patch: Record<string, string | number>) => {
    const next = new URLSearchParams(latest.current.params);
    for (const [k, v] of Object.entries(patch)) {
      if (v === "" || v === undefined) next.delete(k);
      else next.set(k, String(v));
    }
    if (!("page" in patch)) next.delete("page");
    latest.current.params = next;
    latest.current.setParams(next, { replace: true });
  };
  useEffect(() => {
    const t = setTimeout(() => { if (searchInput !== filters.search) update({ search: searchInput }); }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- debounce on input only
  }, [searchInput]);

  const query = useQuery({
    queryKey: ["calls", "history", tab, filters],
    queryFn: () =>
      get<Page<CallListItem> & { scope: string }>(`/calls${qs({
        missed: tab === "missed", direction: filters.direction, status: filters.status, membershipId: filters.membershipId, teamId: filters.teamId,
        phoneNumberId: filters.phoneNumberId,
        from: filters.from ? new Date(filters.from).toISOString() : undefined,
        to: filters.to ? new Date(`${filters.to}T23:59:59`).toISOString() : undefined,
        search: filters.search, sort: filters.sort, page: filters.page, pageSize: 25,
      })}`),
    placeholderData: keepPreviousData,
  });
  const numbers = useQuery({ queryKey: ["calls", "numbers"], queryFn: () => get<{ items: InboxNumber[] }>("/calls/numbers"), staleTime: 60_000 });
  const myNumbers = numbers.data?.items ?? [];
  const labelOf = new Map(myNumbers.map((n) => [n.e164, n.label]));
  const broadScope = canAny("calls.read.team", "calls.read.organization");
  const members = useQuery({ queryKey: ["members", "all"], queryFn: () => get<Page<Member>>("/members?pageSize=100"), enabled: broadScope && can("users.read") });
  const teams = useQuery({ queryKey: ["teams"], queryFn: () => get<{ items: Team[] }>("/teams"), enabled: broadScope && can("teams.read") });

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader title="Call history" description={query.data ? `Showing ${query.data.scope === "organization" ? "all organization" : query.data.scope === "team" ? "your and your teams'" : "your"} calls.` : undefined} />
      <div role="tablist" aria-label="Call history views" className="mb-4 flex gap-1">
        {(["all", "missed"] as const).map((t) => (
          <button key={t} role="tab" aria-selected={tab === t} onClick={() => update({ tab: t === "all" ? "" : t })}
            className={`focus-ring rounded-xl px-3 py-1.5 text-sm font-medium ${tab === t ? "bg-accent-gradient text-white shadow-glow" : "text-muted hover:bg-white/70"}`}>
            {t === "all" ? "All calls" : "Missed calls"}
          </button>
        ))}
      </div>
      <Card>
        <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <TextField label="Search" placeholder="Number or contact" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} />
          {myNumbers.length > 1 && (
            <SelectField label="Number" value={filters.phoneNumberId} onChange={(e) => update({ phoneNumberId: e.target.value })}>
              <option value="">All my numbers ({myNumbers.length})</option>
              {myNumbers.map((n) => <option key={n.id} value={n.id}>{n.label} · {formatPhone(n.e164)}</option>)}
            </SelectField>
          )}
          <SelectField label="Direction" value={filters.direction} onChange={(e) => update({ direction: e.target.value })}>
            <option value="">Any</option><option value="INBOUND">Inbound</option><option value="OUTBOUND">Outbound</option>
          </SelectField>
          <SelectField label="Outcome" value={filters.status} onChange={(e) => update({ status: e.target.value })}>
            <option value="">Any</option>
            {(["COMPLETED", "NO_ANSWER", "BUSY", "FAILED", "CANCELED"] as const).map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
          </SelectField>
          <SelectField label="Sort by" value={filters.sort} onChange={(e) => update({ sort: e.target.value })}>
            <option value="createdAt">Newest first</option><option value="durationSeconds">Longest first</option>
          </SelectField>
          <TextField label="From date" type="date" value={filters.from} onChange={(e) => update({ from: e.target.value })} />
          <TextField label="To date" type="date" value={filters.to} onChange={(e) => update({ to: e.target.value })} />
          {members.data && (
            <SelectField label="User" value={filters.membershipId} onChange={(e) => update({ membershipId: e.target.value })}>
              <option value="">Anyone</option>
              {members.data.items.map((m) => <option key={m.membershipId} value={m.membershipId}>{m.user.name}</option>)}
            </SelectField>
          )}
          {teams.data && (
            <SelectField label="Team" value={filters.teamId} onChange={(e) => update({ teamId: e.target.value })}>
              <option value="">Any team</option>
              {teams.data.items.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </SelectField>
          )}
        </div>
        {query.isLoading ? <Loading /> : query.error ? <ErrorState error={query.error} onRetry={() => void query.refetch()} /> :
          query.data!.items.length === 0 ? <EmptyState title={tab === "missed" ? "No missed calls" : "No calls match these filters"} /> : (
            <>
              <Table label="Calls">
                <thead><tr><Th>Call</Th><Th>When</Th><Th>Handled by</Th><Th>Outcome</Th><Th className="text-right">Duration</Th><Th><span className="sr-only">Actions</span></Th></tr></thead>
                <tbody>
                  {query.data!.items.map((c) => {
                    const Icon = c.internal ? Users : c.missed ? PhoneMissed : c.direction === "INBOUND" ? PhoneIncoming : PhoneOutgoing;
                    // Internal calls: show the other colleague, from your point of view.
                    const colleague = c.internal ? (c.initiatedBy?.membershipId === session?.membershipId ? c.internalTarget : c.initiatedBy) : null;
                    return (
                      <tr key={c.id}>
                        <Td>
                          <Link to={`/history/${c.id}`} className="focus-ring flex items-center gap-2 rounded hover:underline">
                            <Icon className={`h-4 w-4 ${c.missed ? "text-red-600" : "text-subtle"}`} aria-label={c.internal ? "Internal" : c.missed ? "Missed" : c.direction === "INBOUND" ? "Inbound" : "Outbound"} />
                            <span>
                              <span className="block font-medium">{c.internal ? (colleague?.name ?? "Colleague") : c.contact?.name ?? formatPhone(c.externalNumber)}</span>
                              {c.internal && <span className="block text-xs text-subtle">Internal call</span>}
                              {!c.internal && c.contact && <span className="block text-xs text-subtle">{formatPhone(c.externalNumber)}</span>}
                              {!c.internal && myNumbers.length > 1 && !filters.phoneNumberId && (
                                <span className="block text-xs text-subtle">{c.direction === "INBOUND" ? "to" : "from"} {labelOf.get(c.orgNumber) ?? formatPhone(c.orgNumber)}</span>
                              )}
                            </span>
                            {c.recording?.status === "AVAILABLE" && <Mic className="h-3.5 w-3.5 text-subtle" aria-label="Recorded" />}
                            {c.voicemailLeft && <Voicemail className="h-3.5 w-3.5 text-accent-600" aria-label="Voicemail left" />}
                          </Link>
                        </Td>
                        <Td className="whitespace-nowrap">{formatDateTime(c.createdAt)}</Td>
                        <Td>{c.answeredBy?.name ?? c.initiatedBy?.name ?? "—"}</Td>
                        <Td>
                          <Badge tone={statusTone(c)}>{c.missed ? "Missed" : STATUS_LABELS[c.status]}</Badge>
                          {c.endReason && <span className="mt-0.5 block text-xs text-subtle">{endReasonLabel(c.endReason)}</span>}
                        </Td>
                        <Td className="text-right tabular">{formatDuration(c.durationSeconds)}</Td>
                        <Td className="text-right">
                          {can("calls.initiate") && !c.internal && c.externalNumber !== "anonymous" && (
                            <Button size="sm" onClick={() => dial(c.externalNumber)} aria-label={`Call ${c.contact?.name ?? formatPhone(c.externalNumber)}`}>Call</Button>
                          )}
                        </Td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
              <Pagination page={query.data!.page} pageSize={query.data!.pageSize} total={query.data!.total} onPage={(p) => update({ page: p })} />
            </>
          )}
      </Card>
    </div>
  );
}
